import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  AfterViewInit,
  ChangeDetectorRef,
  ViewContainerRef,
} from "@angular/core";
import { Observable, Subject, take, takeUntil } from "rxjs";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { NzTableQueryParams } from "ng-zorro-antd/table";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { TranslateService } from "@ngx-translate/core";
import { NzModalService } from "ng-zorro-antd/modal";
import { Router } from "@angular/router";
import { APP_CONFIGURATION } from "../../../../../../../core/src/lib/core.di";
import { AppConfig } from "../../../../../../../core/src/lib/app-config/config.types";
import { JobOffer } from "../../../../api/job-offer/job-offer.types";
import { CmsNameValue } from "../../../../api/cms/cms.types";
import { CmsCachedService } from "../../../../api/cms/cms-cached.service";
import { PaginatedContent } from "../../../../api/common.types";
import { TalentJobOpportunity } from "../../../../api/talent-job-opportunity/talent-job-opportunity.types";
import { TalentJobOpportunityService } from "../../../../api/talent-job-opportunity/talent-job-opportunity.service";
import { TalentJobOpportunityFormFilter } from "../../../../api/talent-job-opportunity/talent-job-opportunity.types";
import { MessagesService } from "../../../../api/messages/messages.service";
import { CreateConversationDialogComponent } from "../../../../common-componnets/create-conversation-dialog/create-conversation-dialog.component";
import { InitiateConversationRequest, TalentMessageSuggest } from "../../../../api/messages/messages.types";

@Component({
  selector: "mh-talent-job-opportunities",
  templateUrl: "./talent-job-opportunities.component.html",
  styleUrls: ["./talent-job-opportunities.component.less"],
})
export class TalentJobOpportunitiesComponent implements OnInit, OnDestroy, AfterViewInit {
  jobOpportunities?: PaginatedContent<TalentJobOpportunity>;
  filteredJobOpportunities: TalentJobOpportunity[] = [];
  loading = true;

  // Pagination
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;

  filterForm!: UntypedFormGroup;
  preventDoubleFetch = true;
  private readonly destroy$ = new Subject<void>();

  // Track expanded descriptions
  expandedDescriptions = new Set<string>();
  // Track which descriptions need show more button
  showMoreButtons = new Set<string>();

  sectors$: Observable<Array<CmsNameValue>>;
  industries$: Observable<Array<CmsNameValue>>;

  constructor(
    private talentJobOpportunityService: TalentJobOpportunityService,
    private cmsCachedService: CmsCachedService,
    private fb: UntypedFormBuilder,
    private notificationService: NzNotificationService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private messagesService: MessagesService,
    private modal: NzModalService,
    private viewContainerRef: ViewContainerRef,
    private router: Router,
    @Inject(APP_CONFIGURATION) readonly appConfig: AppConfig,
  ) {
    this.sectors$ = this.cmsCachedService.getUserDataByName("sector");
    this.industries$ = this.cmsCachedService.getUserDataByName("industry");
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadJobOpportunities();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.checkDescriptionOverflows(), 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.filterForm = this.fb.group({
      searchTerm: [""],
      sector: [null],
      industry: [null],
    });
  }

  private loadJobOpportunities(): void {
    if (!this.appConfig.featureStatus.jobOpportunityPublishing) {
      this.loading = false;
      return;
    }

    this.loading = true;
    const formValues = this.getFilterFormValues();

    this.talentJobOpportunityService
      .searchTalentJobOpportunitiesFromForm(formValues, {
        page: this.currentPage,
        itemsOnPage: this.pageSize,
      })
      .pipe(take(1))
      .subscribe({
        next: (paginatedContent) => {
          this.jobOpportunities = paginatedContent;
          this.filteredJobOpportunities = [...paginatedContent.items];
          this.totalItems = paginatedContent.paging.items;
          this.loading = false;
          // Check description overflows after data loads
          setTimeout(() => this.checkDescriptionOverflows(), 100);
        },
        error: () => {
          this.loading = false;
          this.notificationService.error("", this.translateService.instant("notification.error"));
        },
      });
  }

  private getFilterFormValues(): TalentJobOpportunityFormFilter {
    const formValues = this.filterForm?.value || {};
    return {
      searchTerm: formValues.searchTerm || "",
      sector: formValues.sector || null,
      industry: formValues.industry || null,
    };
  }

  submitFilterForm(): void {
    this.currentPage = 1; // Reset to first page when applying filters
    this.loadJobOpportunities();
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    if (this.loading) return;
    if (this.preventDoubleFetch) {
      this.preventDoubleFetch = false;
      return;
    }

    const { pageSize, pageIndex } = params;
    this.currentPage = pageIndex;
    this.pageSize = pageSize;
    this.loadJobOpportunities();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadJobOpportunities();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1;
    this.loadJobOpportunities();
  }

  // Convert TalentJobOpportunity to JobOffer format for location component
  asJobOffer(jobOpportunity: TalentJobOpportunity): JobOffer {
    return {
      id: jobOpportunity.jobOpportunityId,
      missionPartnerId: jobOpportunity.missionPartnerId || "",
      conversationId: jobOpportunity.conversationId || "",
      missionPartnerName: "",
      archived: false,
      name: jobOpportunity.jobOpportunityName,
      description: jobOpportunity.description || "",
      locations:
        jobOpportunity.locations?.map((loc) => ({
          postalCode: loc.postalCode,
          country: loc.country,
          city: loc.city,
        })) || [],
      jobSharing: false,
      matchedAmount: 0,
      creationTimestamp: jobOpportunity.creationTimestamp,
      status: 0 as any, // JobOpportunitySearchResultStatus
      sectorFocus: jobOpportunity.sectorFocus || [],
      industries: jobOpportunity.industries || [],
      domainExpertise: jobOpportunity.domainExpertise || [],
      department: jobOpportunity.department || [],
      careerLevel: jobOpportunity.careerLevel || [],
      workingModes: jobOpportunity.workingModes || [],
    } as JobOffer;
  }

  // Description expansion methods
  isDescriptionExpanded(jobOpportunityId: string): boolean {
    return this.expandedDescriptions.has(jobOpportunityId);
  }

  shouldShowMoreButton(jobOpportunityId: string): boolean {
    return this.showMoreButtons.has(jobOpportunityId);
  }

  toggleDescriptionExpansion(jobOpportunityId: string): void {
    if (this.expandedDescriptions.has(jobOpportunityId)) {
      this.expandedDescriptions.delete(jobOpportunityId);
    } else {
      this.expandedDescriptions.add(jobOpportunityId);
    }
  }

  private checkDescriptionOverflows(): void {
    this.showMoreButtons.clear();

    this.filteredJobOpportunities.forEach((jobOpportunity) => {
      const element = document.querySelector(`[data-job-id="${jobOpportunity.jobOpportunityId}"] .description-text`);
      if (element) {
        const isOverflowing = element.scrollHeight > element.clientHeight;
        if (isOverflowing) {
          this.showMoreButtons.add(jobOpportunity.jobOpportunityId);
        }
      }
    });

    this.cdr.detectChanges();
  }

  onStartConversation(jobOpportunity: TalentJobOpportunity, event?: Event): void {
    console.log("onStartConversation", jobOpportunity);
    if (event) event.stopPropagation();

    if (jobOpportunity.conversationId) {
      // If conversation already exists, navigate to it
      this.onToConversation(jobOpportunity, event);
    } else {
      // Otherwise, start a new conversation
      this.openCreateConversationModal(jobOpportunity);
    }
  }

  onToConversation(jobOpportunity: TalentJobOpportunity, event?: Event) {
    if (event) event.stopPropagation();

    if (jobOpportunity.conversationId) {
      // Navigate to existing conversation
      this.router.navigate(["/", "talent", "inbox", "conversation", jobOpportunity.conversationId], {
        queryParams: { origin: "job-opportunities", originId: jobOpportunity.jobOpportunityId },
      });
    } else {
      // If no conversation exists, start a new one
      this.openCreateConversationModal(jobOpportunity);
    }
  }

  openCreateConversationModal(jobOpportunity: TalentJobOpportunity) {
    if (!jobOpportunity.missionPartnerId) {
      this.notificationService.error("", this.translateService.instant("notification.error"));
      return;
    }

    const modal = this.modal.create({
      nzContent: CreateConversationDialogComponent,
      nzViewContainerRef: this.viewContainerRef,
      nzData: {
        suggestRequest: {
          missionPartnerId: jobOpportunity.missionPartnerId,
        } as TalentMessageSuggest,
      },
      nzWidth: 800,
      nzOnOk: (modalCmp) => this.doCreateConversation(jobOpportunity, modalCmp.formGroup.value),
    });
  }

  doCreateConversation(jobOpportunity: TalentJobOpportunity, formData: Partial<InitiateConversationRequest>) {
    if (!jobOpportunity.missionPartnerId) {
      this.notificationService.error("", this.translateService.instant("notification.error"));
      return;
    }

    this.messagesService
      .initiateConversation(
        jobOpportunity.missionPartnerId,
        formData.messageText as string,
        jobOpportunity.jobOpportunityId,
      )
      .subscribe({
        next: (conversation) => {
          if (conversation) {
            this.notificationService.success("", this.translateService.instant("notification.success"));
            // Update the job opportunity with the new conversation ID
            jobOpportunity.conversationId = conversation.id;
            // Navigate to the conversation
            this.router.navigate(["/", "talent", "inbox", "conversation", conversation.id]);
          } else {
            this.notificationService.error("", this.translateService.instant("notification.error"));
          }
        },
        error: () => {
          this.notificationService.error("", this.translateService.instant("notification.error"));
        },
      });
  }
}
